import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { authService, LoginCredentials, Login2faRequiredResponse } from '../services/authService';
import { subscribeAuthTokenSync } from '../services/authTokenSync';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { canAccessModule } from './authPermissions';
import { useSessionHeartbeat } from '../hooks/useSessionHeartbeat';
import { isTransientApiError } from '../utils/apiErrors';

export interface User {
  id: number;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userDisplayName: string;
  userPhotoPath?: string | null;
  role?: 'ADMIN' | 'USER' | 'CLIENT' | 'GUEST';
  totpEnabled?: boolean;
  canAssignEmailsToOthers?: boolean;
  displayTimeZone?: string | null;
}

export type ModulePermissionAction = 'view' | 'edit' | 'delete';

export interface ModulePermissionFlags {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export type PermissionsMap = Record<string, ModulePermissionFlags>;

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  permissions: PermissionsMap;
  permissionsLoaded: boolean;
  can: (moduleKey: string, action: ModulePermissionAction) => boolean;
  login: (credentials: LoginCredentials) => Promise<Login2faRequiredResponse | void>;
  verify2fa: (params: { pendingToken: string; code?: string; recoveryCode?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const emptyPermissions: PermissionsMap = {};

const REFRESH_INTERVAL_MS = 12 * 60 * 1000;
const REFRESH_RETRY_MS = 60 * 1000;

const completePostLogin = async (): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, 300));
  try {
    await api.get('/settings/user/view-preferences');
    window.dispatchEvent(new CustomEvent('viewPreferencesUpdated'));
    window.dispatchEvent(new CustomEvent('userAuthenticated'));
  } catch (error) {
    console.error('Failed to fetch view preferences on login:', error);
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<PermissionsMap>(emptyPermissions);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const navigate = useNavigate();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleTokenRefreshRef = useRef<(delayMs?: number) => void>(() => {});

  useSessionHeartbeat(isAuthenticated);

  const clearAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setPermissions(emptyPermissions);
    setPermissionsLoaded(false);
  }, []);

  const fetchUser = async (): Promise<void> => {
    try {
      const response = await api.get<User>('/profile');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
    }
  };

  const fetchPermissions = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<PermissionsMap>('/permissions');
      setPermissions(response.data ?? emptyPermissions);
      setPermissionsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions(emptyPermissions);
      setPermissionsLoaded(true);
    }
  }, []);

  const scheduleTokenRefresh = useCallback(
    (delayMs = REFRESH_INTERVAL_MS) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      refreshTimerRef.current = setTimeout(async () => {
        const result = await authService.refreshAccessToken();
        if (result.status === 'success') {
          scheduleTokenRefreshRef.current(REFRESH_INTERVAL_MS);
          return;
        }
        if (authService.getRefreshToken()) {
          scheduleTokenRefreshRef.current(REFRESH_RETRY_MS);
          return;
        }
        clearAuthState();
        navigate('/login');
      }, delayMs);
    },
    [clearAuthState, navigate],
  );

  scheduleTokenRefreshRef.current = scheduleTokenRefresh;

  const can = useCallback(
    (moduleKey: string, action: ModulePermissionAction): boolean =>
      canAccessModule(user, permissions, moduleKey, action),
    [user, permissions],
  );

  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getToken();
      if (token) {
        try {
          await authService.validateSession();
          setIsAuthenticated(true);
          await fetchUser();
          await fetchPermissions();
          scheduleTokenRefresh();
          try {
            await api.get('/settings/user/view-preferences');
          } catch (error) {
            console.error('Failed to fetch view preferences on session check:', error);
          }
        } catch (error) {
          if (isTransientApiError(error)) {
            setIsAuthenticated(true);
            try {
              await fetchUser();
              await fetchPermissions();
              scheduleTokenRefresh();
            } catch {
              // Profile/permissions may also fail during outage — keep session.
            }
          } else {
            const result = await authService.refreshAccessToken();
            if (result.status === 'success') {
              setIsAuthenticated(true);
              await fetchUser();
              await fetchPermissions();
              scheduleTokenRefresh();
            } else if (!authService.getRefreshToken()) {
              clearAuthState();
              authService.clearTokens();
            } else {
              setIsAuthenticated(true);
              scheduleTokenRefresh(REFRESH_RETRY_MS);
            }
          }
        }
      } else {
        const result = await authService.refreshAccessToken();
        if (result.status === 'success') {
          setIsAuthenticated(true);
          await fetchUser();
          await fetchPermissions();
          scheduleTokenRefresh();
        } else if (authService.getRefreshToken()) {
          setIsAuthenticated(true);
          scheduleTokenRefresh(REFRESH_RETRY_MS);
        } else {
          clearAuthState();
        }
      }
      setIsLoading(false);
    };

    checkAuth();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [clearAuthState, fetchPermissions, scheduleTokenRefresh]);

  useEffect(() => {
    return subscribeAuthTokenSync((message) => {
      if (message.type === 'tokens-cleared') {
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        clearAuthState();
        if (window.location.pathname !== '/login') {
          navigate('/login');
        }
        return;
      }
      if (message.type === 'tokens-updated' && authService.getRefreshToken()) {
        setIsAuthenticated(true);
        scheduleTokenRefresh(REFRESH_INTERVAL_MS);
      }
    });
  }, [clearAuthState, navigate, scheduleTokenRefresh]);

  const finalizeLogin = async () => {
    setIsAuthenticated(true);
    await fetchUser();
    await fetchPermissions();
    await completePostLogin();
    scheduleTokenRefresh();
    navigate('/');
  };

  const login = async (credentials: LoginCredentials): Promise<Login2faRequiredResponse | void> => {
    const response = await authService.login(credentials);
    if ('requires2fa' in response && response.requires2fa) {
      return response;
    }
    if ('access_token' in response) {
      authService.setTokens(response.access_token, response.refresh_token);
      await finalizeLogin();
    }
  };

  const verify2fa = async (params: {
    pendingToken: string;
    code?: string;
    recoveryCode?: string;
  }) => {
    const response = await authService.verify2fa(params.pendingToken, params.code, params.recoveryCode);
    authService.setTokens(response.access_token, response.refresh_token);
    await finalizeLogin();
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      clearAuthState();
      navigate('/login');
    }
  };

  const refreshUser = async () => {
    if (isAuthenticated) {
      await fetchUser();
      await fetchPermissions();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        permissions,
        permissionsLoaded,
        can,
        login,
        verify2fa,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
